/* Full turn or half turn, by what the client is paying for the sitting.
 *
 * Kevin, 2026-10-23: a square is a full turn when its price reaches the salon's
 * cutoff, and a half turn when it does not — both ways, so a cheap service set
 * up as a full-turn type comes down to a half as surely as a half-turn service
 * with enough added to it goes up. Forty dollars unless the admin app sets
 * `fullTurnPrice`.
 *
 * The price is the square's own booked line, which Mangomint already prices
 * with its options in it (a $30 Gel Polish Change with Chrome is a $45 line),
 * plus every add-on line folded onto the square — a $35 manicure with a $10 Gel
 * Removal added at the chair is a $45 sitting.
 *
 * Nothing here decides when the price is not known. A walk-in with no booking,
 * or a line stored before prices were, keeps whatever its turn type says — as
 * does a type set to count for nothing (a no-charge rework is not half a turn
 * because it costs nothing).
 */

export var DEFAULT_FULL_TURN_PRICE = 40;
var MAX_FULL_TURN_PRICE = 1000;

// The cutoff as stored, made safe: a positive number of dollars, else the
// default. Cents are kept — $39.50 is a price a salon can mean.
export function cleanCutoff(v) {
  var n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  if (typeof n !== 'number' || !isFinite(n) || n <= 0) return DEFAULT_FULL_TURN_PRICE;
  return Math.min(Math.round(n * 100) / 100, MAX_FULL_TURN_PRICE);
}

function money(v) {
  if (v == null || v === '') return null;
  var n = Number(v);
  return isFinite(n) ? n : null;
}

// What the sitting costs: the square's own line plus its add-ons, or null when
// the square's own line has no price. An add-on whose price is unknown adds
// nothing rather than making the whole sitting unknown.
export function sittingPrice(ownPrice, addOnPrices) {
  var own = money(ownPrice);
  if (own == null) return null;
  return (addOnPrices || []).reduce(function (sum, p) {
    var n = money(p);
    return n == null ? sum : sum + n;
  }, own);
}

/* One technician's squares on one visit, counted as one sitting.
 *
 * Kevin, 2026-10-23: Thu's 12:20 Walk In was a Classic Manicure ($25) and a
 * Classic Pedicure ($40) — one client, one technician, and that is one full
 * turn, not one and a half. But Yulisa's Hard Gel Fill and Gel Pedicure on one
 * visit are two pieces of real work and stay two turns. So:
 *
 *   - every square that reaches the cutoff on its own is a full turn;
 *   - squares under it ride along for nothing next to one of those;
 *   - with no full one in the sitting, the small ones' total decides: a full
 *     turn at the cutoff, a half below it.
 *
 * Drawn as halves in one square when it can be: when the sitting is worth half
 * a turn for every square in it (Thu's two squares, one turn), each square is a
 * half and the board pairs consecutive halves into one square (`P/M`, worth 1).
 * Otherwise full ones are 1, the small ones riding along are 0, and a sitting
 * of only small ones puts its weight on its first square.
 *
 *   squares  [{ id, startedAt, prices: [], typeWeight, handWeighed }]
 *            one technician's squares on one appointment. `prices` are the
 *            booked lines on the square, its add-ons included; `handWeighed`
 *            when the desk set the weight on the merge sheet.
 *   cutoff   the salon's full-turn price
 *
 * Returns { id: weight } for the squares to set, or {} to leave them all as
 * they are: a square with no price known, or one the desk weighed by hand. A
 * square whose type counts for nothing is left out of the sitting altogether.
 */
export function visitWeights(squares, cutoff) {
  var list = (squares || []).filter(function (s) { return s && s.typeWeight !== 0; });
  if (!list.length) return {};
  if (list.some(function (s) { return s.handWeighed; })) return {};
  var priced = list.map(function (s) {
    return { s: s, price: sittingPrice(firstKnown(s.prices), restOf(s.prices)) };
  });
  if (priced.some(function (x) { return x.price == null; })) return {};

  var full = priced.filter(function (x) { return priceWeight(x.price, cutoff, 1) === 1; });
  var small = priced.filter(function (x) { return full.indexOf(x) === -1; });
  var smallTotal = small.reduce(function (sum, x) { return sum + x.price; }, 0);
  var smallWeight = full.length || !small.length ? 0 : priceWeight(smallTotal, cutoff, 1);
  var total = full.length + smallWeight;

  var ordered = priced.slice().sort(function (a, b) {
    return (a.s.startedAt || 0) - (b.s.startedAt || 0) ||
      String(a.s.id).localeCompare(String(b.s.id));
  });
  var out = {};
  if (ordered.length > 1 && total * 2 === ordered.length) {
    ordered.forEach(function (x) { out[x.s.id] = 0.5; });
    return out;
  }
  var firstSmall = ordered.find(function (x) { return small.indexOf(x) !== -1; });
  ordered.forEach(function (x) {
    out[x.s.id] = full.indexOf(x) !== -1 ? 1 : x === firstSmall ? smallWeight : 0;
  });
  return out;
}

// A square's price is its lines added up, unknown only when none is known.
function firstKnown(prices) {
  var list = (prices || []).filter(function (p) { return money(p) != null; });
  return list.length ? list[0] : null;
}
function restOf(prices) {
  var list = (prices || []).filter(function (p) { return money(p) != null; });
  return list.slice(1);
}

/* Does this one booked line count as a full turn on its own? The same answer a
 * square of its own would get: the cutoff when the price is known, its type when
 * it is not, and never for a type that counts for nothing. `typeWeight` is null
 * for a line not mapped to a type. */
export function lineIsFullTurn(price, typeWeight, cutoff) {
  var weight = priceWeight(money(price), cutoff, typeWeight);
  return weight != null ? weight === 1 : typeWeight != null && typeWeight >= 1;
}

// 1, 0.5, or null for "leave the weight to the turn type".
export function priceWeight(price, cutoff, typeWeight) {
  if (price == null || !isFinite(price)) return null;
  if (typeWeight === 0) return null;
  // Compared in cents, so $39.999 of floating point never misses $40.
  return Math.round(price * 100) >= Math.round(cleanCutoff(cutoff) * 100) ? 1 : 0.5;
}
